import { CloseButton } from "@components/buttons/close";
import {
  SHButton,
  SHDialog,
  SHRichTextEditor,
  SHStack,
} from "@components/design-systems";
import { AnalysisSummaryStaticValueConfigs } from "@pages/platform-analysis-v2/_id/steps/summary/configs";
import { useMemo, useState } from "react";
import { useToggle } from "react-use";

type AnalysisDecisionCommentDialogProps = {
  open: boolean;
  originalComment?: string | null;
  onClose?: () => void;
  onSubmit?: (comment: string) => void;
};

export const AnalysisDecisionCommentDialog = ({
  open,
  originalComment,
  onClose,
  onSubmit,
}: AnalysisDecisionCommentDialogProps) => {
  const [value, setValue] = useState<string>(originalComment || "");
  const [isSubmitting, toggleIsSubmitting] = useToggle(false);

  const handleSubmit = () => {
    toggleIsSubmitting(true);
    onSubmit?.(value);
    toggleIsSubmitting(false);
  };

  const shouldDisableSaveButton = useMemo(() => {
    return isSubmitting || value?.trim() === (originalComment || "")?.trim();
  }, [value, originalComment, isSubmitting]);

  return (
    <SHDialog
      open={open}
      keepMounted={false}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth={false}
      sx={{
        "& .MuiPaper-root": {
          border: 0,
          width: 541,
          paddingTop: "0px",
          paddingBottom: "20px",
        },
      }}
      header={
        <SHStack sx={{ position: "absolute", top: 11, right: 11 }}>
          <CloseButton width="12px" height="12px" onClick={onClose} />
        </SHStack>
      }
      body={
        <SHRichTextEditor
          value={value}
          onChange={(newValue) => setValue(newValue)}
          maxLength={AnalysisSummaryStaticValueConfigs.MaximumCharacterComment}
          placeHolder={"Enter your comment"}
          height={200}
          label={"Add comment"}
          sx={{ marginTop: 0 }}
          isCommentBox={true}
        />
      }
      footer={
        <SHStack
          justifyContent="center"
          alignItems="center"
          direction={"row"}
          spacing={7}
        >
          <SHButton
            color="secondary"
            variant="text"
            size="extraMedium"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </SHButton>
          <SHButton
            color="primary"
            size="extraMedium"
            variant="contained"
            onClick={handleSubmit}
            disabled={shouldDisableSaveButton}
            isLoading={isSubmitting}
          >
            Save
          </SHButton>
        </SHStack>
      }
    />
  );
};
